/**
 * **What a task card shows under its heading, per kind** — the one content three surfaces draw at
 * three densities (`desk` on `/zadania`, `quick` in the client's Przegląd, `short` on the Pulpit).
 * KANON "Karty zadań", 24 September 2026.
 *
 * It owns the card's writes in flight (`useTaskAct`), so a card that is swapped for another starts
 * clean — every host keys the card by its item.
 *
 * What it does NOT own: the writes themselves and the two nodes other features draw (the note
 * composer, the survey's answers) — they arrive through `slots`, see `lib/taskWrites`.
 */

import { useTranslation } from 'react-i18next';

import { toIsoDate } from '~/shared/lib/calendarDate';

import { useTaskAct } from '../lib/useTaskAct';
import type { TaskDensity, TaskSlots } from '../lib/taskWrites';
import type { AttentionItem } from '../model/attentionItems';
import { vitalKindOf, type TaskContent } from '../model/taskContent';
import { AbandonedBlock } from './AbandonedBlock';
import { ClipBlock } from './ClipBlock';
import { GoalBlock } from './GoalBlock';
import { InvitationBlock } from './InvitationBlock';
import { MessageBlock } from './MessageBlock';
import { MonthBlock } from './MonthBlock';
import { PlanBlock } from './PlanBlock';
import { RecordBlock } from './RecordBlock';
import { SessionBlock } from './SessionBlock';
import { VitalBlock } from './VitalBlock';
import { WeekStrip } from './WeekStrip';
import styles from './taskBlocks.module.css';

export interface TaskBodyProps {
  readonly item: AttentionItem;
  readonly density: TaskDensity;
  /** The card's one figure and line — `useTaskContent`, computed once by the card. */
  readonly content: TaskContent;
  /** The surface's clock — the day the weeks and months are drawn around. */
  readonly now: Date;
  readonly slots: TaskSlots;
  readonly reducedMotion: boolean;
}

/**
 * Whether the card's meta line stands in the BODY rather than under the name: on the Pulpit's card
 * a session and a clip draw a visual on the left — the ring, the poster — and the column beside it
 * opens with the line ("Dół B · 58 min"), so the head says only who and what (owner, 26 September
 * 2026). The card asks this so the line is drawn once.
 */
export function bodyCarriesGlance(item: AttentionItem, density: TaskDensity): boolean {
  return density === 'short' && (item.detail.kind === 'session' || item.detail.kind === 'clip' || item.detail.kind === 'clipComment');
}

export function TaskBody({ item, density, content, now, slots, reducedMotion }: TaskBodyProps) {
  const { t } = useTranslation();
  const act = useTaskAct({
    onResolved: () => {
      slots.onResolved?.(item);
    },
    announce: slots.announce,
  });
  const todayIso = toIsoDate(now);
  const detail = item.detail;
  const glance = bodyCarriesGlance(item, density) ? content.glance : '';

  switch (item.reason) {
    case 'sessionAwaitingReview':
      return detail.kind === 'session' ? (
        <SessionBlock
          session={detail}
          density={density}
          glance={glance}
          reducedMotion={reducedMotion}
          note={
            density === 'desk'
              ? slots.note?.(item, [t('web:tasks.blocks.session.draftPraise'), t('web:tasks.blocks.session.draftNext')])
              : undefined
          }
        />
      ) : null;
    case 'surveyAwaitingReview':
      return item.action.kind === 'markReviewed' && item.subjectCustomerId !== null ? (
        <>
          {slots.survey(
            { customerId: item.subjectCustomerId, surveyAssignmentId: item.action.surveyAssignmentId },
            density,
          )}
        </>
      ) : null;
    case 'newClip':
    case 'clipCommentAwaitingReply':
      return (
        <ClipBlock item={item} density={density} glance={glance} openClip={slots.openClip} player={slots.clipPlayer} />
      );
    case 'abandonedWorkout':
      return <AbandonedBlock item={item} density={density} />;
    case 'missedWorkout':
      return (
        <div className={`${String(styles.stack)} ${String(styles[density])}`}>
          {/* Which session was missed, as a tile at the top of the desk card (owner, 26 September
            * 2026). The plan day's name, where one named it. */}
          {density === 'desk' && item.triage.sessionName !== null ? (
            <div className={styles.tiles}>
              <div className={styles.tile}>
                <p className={styles.tileLabel}>{t('web:tasks.fact.session')}</p>
                <p className={styles.tileValue}>{item.triage.sessionName}</p>
              </div>
            </div>
          ) : null}
          <WeekStrip
            customerId={item.subjectCustomerId}
            taskDay={item.triage.sessionLocalDay}
            todayIso={todayIso}
            compact={density !== 'desk'}
          />
        </div>
      );
    case 'lapsedActivity':
    case 'noUpcomingSessions':
      return <MonthBlock item={item} density={density} todayIso={todayIso} slots={slots} act={act} />;
    case 'planEndingSoon':
    case 'planCompletionDue':
    case 'planCompleted':
      return <PlanBlock item={item} density={density} hero={content.hero} slots={slots} act={act} />;
    case 'goalRenewalDue':
      return (
        <GoalBlock item={item} density={density} todayIso={todayIso} slots={slots} act={act} reducedMotion={reducedMotion} />
      );
    case 'invitationPending':
      return <InvitationBlock item={item} density={density} todayIso={todayIso} slots={slots} act={act} />;
    case 'unreadMessage':
      return <MessageBlock item={item} density={density} />;
    case 'lowSleep':
    case 'lowEnergy': {
      const kind = vitalKindOf(item.reason);
      return item.triage.vital === null || kind === null ? null : (
        <VitalBlock item={item} kind={kind} vital={item.triage.vital} density={density} reducedMotion={reducedMotion} />
      );
    }
    case 'prAchieved':
      return item.triage.record === null ? null : (
        <RecordBlock item={item} record={item.triage.record} density={density} content={content} />
      );
    // A lead is read on its own page; the card names who and when, and its foot goes there.
    case 'goalAchieved':
    case 'publicFormLeadSubmitted':
      return null;
  }
}
